import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { Store, Product, Gift } from '../../types';
import { STORES, PRODUCTS, MOCK_GIFTS } from '../../data/mockData';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useToast } from '../ToastContext';
import { Coffee, Heart, ChevronRight, Check, ArrowLeft, ChevronDown, Search, User, PartyPopper, Link as LinkIcon } from 'lucide-react';
import { ProductCard } from '../ProductCard';
interface GiftingScreenProps {
  onNavigate: (screen: string, data?: any) => void;
  prefilledStore?: {
    storeId: string | null;
    storeName: string | null;
    storeCode: string | null;
  };
  onClearPrefill?: () => void;
}
type GiftingStep = 1 | 2 | 3 | 4 | 5;
export function GiftingScreen({
  onNavigate,
  prefilledStore,
  onClearPrefill
}: GiftingScreenProps) {
  const [step, setStep] = useState<GiftingStep>(1);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [selectedItem, setSelectedItem] = useState<Product | null>(null);
  const [recipient, setRecipient] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [showReceivedExpanded, setShowReceivedExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [recipientMode, setRecipientMode] = useState<'contacts' | 'userid'>('contacts');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const {
    showToast
  } = useToast();
  // Anonymous slider drag state
  const sliderTrackRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0); // 0 = left (not anon), 1 = right (anon)
  const handleSliderPointerDown = useCallback((e: React.PointerEvent) => {
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);
  const handleSliderPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging || !sliderTrackRef.current) return;
    const rect = sliderTrackRef.current.getBoundingClientRect();
    const thumbRadius = 24;
    const trackInner = rect.width - thumbRadius * 2;
    const x = e.clientX - rect.left - thumbRadius;
    const progress = Math.max(0, Math.min(1, x / trackInner));
    setDragProgress(progress);
  }, [isDragging]);
  const handleSliderPointerUp = useCallback(() => {
    setIsDragging(false);
    const snappedAnon = dragProgress > 0.5;
    setIsAnonymous(snappedAnon);
    setDragProgress(snappedAnon ? 1 : 0);
  }, [dragProgress]);
  const toggleAnonymous = useCallback(() => {
    const newVal = !isAnonymous;
    setIsAnonymous(newVal);
    setDragProgress(newVal ? 1 : 0);
  }, [isAnonymous]);
  const receivedGifts = MOCK_GIFTS.filter(g => g.recipient === 'You');
  const pendingCount = receivedGifts.filter(g => g.status === 'pending').length;
  // Auto-populate on mount if prefilled data exists
  useEffect(() => {
    if (prefilledStore?.storeName && prefilledStore?.storeId) {
      setSearchQuery(prefilledStore.storeName);
      // Auto-select store if it exists in the list
      const matchingStore = STORES.find(s => s.id === prefilledStore.storeId || s.name === prefilledStore.storeName || s.storeId === prefilledStore.storeCode);
      if (matchingStore) {
        setSelectedStore(matchingStore);
        setActiveCategory('All');
        setStep(2); // Skip to item selection step
        showToast(`Ready to send a treat from ${matchingStore.name}! ☕️`, 'success');
      }
    }
  }, [prefilledStore]);
  // Mock contacts
  const mockContacts = [{
    id: '1',
    name: 'Emma Thompson',
    phone: '+1 (555) 123-4567'
  }, {
    id: '2',
    name: 'James Wilson',
    phone: '+1 (555) 234-5678'
  }, {
    id: '3',
    name: 'Sophia Chen',
    phone: '+1 (555) 345-6789'
  }, {
    id: '4',
    name: 'Marcus Johnson',
    phone: '+1 (555) 456-7890'
  }];
  const handleStoreSelect = (store: Store) => {
    setSelectedStore(store);
    setActiveCategory('All');
    setStep(2);
    // Clear prefill when user manually selects
    if (onClearPrefill) {
      onClearPrefill();
    }
  };
  const handleItemSelect = (product: Product) => {
    setSelectedItem(product);
    setStep(3);
  };
  const handleContinueToConfirm = () => {
    if (!recipient) {
      showToast('Please select a recipient', 'error');
      return;
    }
    setStep(4);
  };
  const handleSendGift = () => {
    setStep(5); // Go to success screen
  };
  const handleBackToHome = () => {
    // Reset all state
    setStep(1);
    setSelectedStore(null);
    setSelectedItem(null);
    setRecipient('');
    setRecipientPhone('');
    setMessage('');
    setActiveCategory('All');
    setSearchQuery('');
    if (onClearPrefill) {
      onClearPrefill();
    }
    onNavigate('home');
  };
  const handleBack = () => {
    if (step === 2) {
      setSelectedStore(null);
      setStep(1);
      if (onClearPrefill) {
        onClearPrefill();
      }
    } else if (step === 3) {
      setSelectedItem(null);
      setStep(2);
    } else if (step === 4) {
      setStep(3);
    } else if (step === 5) {
      handleBackToHome();
    }
  };
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    // Clear prefill when user manually changes search
    if (onClearPrefill) {
      onClearPrefill();
    }
  };
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const filteredStores = STORES.filter(store => store.name.toLowerCase().includes(searchQuery.toLowerCase()) || store.storeId.toLowerCase().includes(searchQuery.toLowerCase()));
  const [message, setMessage] = useState('');
  // Step 5: Success Screen
  if (step === 5) {
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-[#FFFC00] flex flex-col" data-id="element-757">
        {/* Back Button */}
        <button onClick={handleBack} className="mb-6 w-12 h-12 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-758">
          <ArrowLeft size={24} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-759" />
        </button>

        {/* Success Content */}
        <div className="flex-1 flex flex-col items-center justify-start pt-12" data-id="element-760">
          {/* Success Icon */}
          <div className="w-32 h-32 bg-lime-200 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500" data-id="element-761">
            <PartyPopper size={64} className="text-lime-600" data-id="element-762" />
          </div>

          {/* Success Message */}
          <div className="text-center mb-8" data-id="element-763">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2" data-id="element-764">
              🎉 Treat Sent!
            </h1>
            <p className="text-gray-700 text-base" data-id="element-765">
              Your treat has been sent to
            </p>
          </div>

          {/* Recipient Card */}
          <div className="w-full bg-white rounded-2xl p-5 mb-4 text-center shadow-lg" data-id="element-766">
            <p className="font-bold text-gray-900 text-xl mb-1" data-id="element-767">{recipient}</p>
            <p className="text-gray-500 text-sm" data-id="element-768">{recipientPhone}</p>
          </div>

          {/* Action Buttons */}
          <div className="w-full space-y-3" data-id="element-769">
            <button className="w-full py-4 bg-white text-gray-900 rounded-2xl font-bold text-base active:scale-[0.98] transition-transform flex items-center justify-center gap-2 shadow-sm" data-id="element-770">
              <LinkIcon size={20} data-id="element-771" />
              Share Treat Link
            </button>

            <button onClick={handleBackToHome} className="w-full py-4 bg-black text-[#FFFC00] rounded-2xl font-bold text-base active:scale-[0.98] transition-transform shadow-lg" data-id="element-772">
              Back to Home
            </button>

            <button onClick={() => onNavigate('gift-history')} className="w-full py-3 text-gray-700 font-semibold text-base" data-id="element-773">
              View Treat History
            </button>
          </div>
        </div>
      </div>;
  }
  // Step 1: Store Selection + Treats Received
  if (step === 1) {
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-[#FFFC00]" data-id="element-774">
        {/* Back Button */}
        <button onClick={() => onNavigate('home')} className="mb-6 w-12 h-12 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-775">
          <ArrowLeft size={24} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-776" />
        </button>

        {/* Main Card */}
        <div className="bg-[#FFF9C4] rounded-3xl p-6 mb-6 shadow-2xl" data-id="element-777">
          {/* Header */}
          <div className="mb-6" data-id="element-778">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-779">
              Send a Treat
            </h1>
            <p className="text-gray-700 text-base" data-id="element-780">
              Choose a store and send a treat to friends
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex justify-between mb-6" data-id="element-781">
            {[1, 2, 3].map(stepNumber => <div key={stepNumber} className={`flex items-center justify-center rounded-full w-12 h-12 font-bold text-base transition-all ${stepNumber < step ? 'bg-black text-[#FFFC00]' : stepNumber === step ? 'bg-[#FFFC00] text-black border-2 border-black' : 'bg-white/50 text-gray-400'}`} data-id="element-782">
                {stepNumber < step ? <Check size={16} strokeWidth={3} data-id="element-783" /> : stepNumber}
              </div>)}
          </div>

          {/* Select a Store Heading */}
          <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-784">
            Select a Store
          </h2>

          {/* Search Bar */}
          <div className="relative mb-4" data-id="element-785">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-786" />
            <input type="text" placeholder="Search by store name or place ID..." value={searchQuery} onChange={handleSearchChange} className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black text-base" data-id="element-787" />
          </div>

          {/* Show prefilled indicator */}
          {prefilledStore?.storeName && !selectedStore && <div className="mb-4 flex items-center text-sm bg-green-50 border border-green-200 rounded-xl p-3" data-id="element-788">
              <Check size={14} className="mr-2 text-green-600 flex-shrink-0" data-id="element-789" />
              <span className="text-green-800" data-id="element-790">
                Ready to treat from{' '}
                <span className="font-semibold" data-id="element-791">
                  {prefilledStore.storeName}
                </span>
              </span>
            </div>}

          {/* Store List (Vertical) */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto" data-id="element-792">
            {filteredStores.map(store => <button key={store.id} onClick={() => handleStoreSelect(store)} className="w-full bg-white rounded-2xl p-4 flex items-center gap-4 text-left active:scale-[0.98] transition-transform shadow-sm" data-id="element-793">
                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-794">
                  <img src={store.image} alt={store.name} className="w-full h-full object-cover" data-id="element-795" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-796">
                  <h3 className="font-bold text-gray-900 text-base mb-1 truncate" data-id="element-797">
                    {store.name}
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-798">ID: {store.storeId}</p>
                </div>
              </button>)}
          </div>
        </div>

        {/* Treats Received Section */}
        <div className="bg-[#A78BFA] rounded-3xl p-6 shadow-xl" data-id="element-799">
          <div className="flex items-center gap-2 mb-3" data-id="element-800">
            <h2 className="text-2xl font-bold text-white" data-id="element-801">Treats Received</h2>
            {pendingCount > 0 && <span className="bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg flex items-center gap-1" data-id="element-802">
                <Coffee size={12} data-id="element-803" />
                {pendingCount}
              </span>}
          </div>
          <p className="text-white/90 text-base mb-4" data-id="element-804">
            Treats you've received from friends
          </p>

          {/* Treats List */}
          <div className="space-y-3 mb-4" data-id="element-805">
            {receivedGifts.map(gift => <div key={gift.id} className={`rounded-2xl p-4 ${gift.status === 'pending' ? 'bg-purple-100' : 'bg-white'}`} data-id="element-806">
                <div className="flex items-start gap-3" data-id="element-807">
                  <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-808">
                    <img src={gift.itemImage} alt={gift.itemName} className="w-full h-full object-cover" data-id="element-809" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-810">
                    <h3 className="font-bold text-gray-900 text-base mb-1 truncate" data-id="element-811">
                      {gift.itemName}
                    </h3>
                    <p className="text-sm text-gray-600 mb-2 truncate" data-id="element-812">
                      {gift.storeName}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-gray-600 mb-2" data-id="element-813">
                      <span className="flex items-center gap-1" data-id="element-814">
                        <Coffee size={12} className="text-[#F7C300]" data-id="element-815" />
                        From {gift.sender}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-816">
                        📅 {formatDate(gift.date)}
                      </span>
                    </div>
                    {gift.status === 'pending' ? <button onClick={() => onNavigate('giftRedeem', {
                  gift,
                  from: 'gifting'
                })} className="px-4 py-2 bg-black text-[#FFFC00] rounded-xl text-sm font-bold active:scale-95 transition-transform" data-id="element-817">
                        Redeem Now
                      </button> : <div className="flex items-center gap-1 text-sm text-green-700 font-medium" data-id="element-818">
                        <Check size={16} className="text-green-700" data-id="element-819" />
                        Redeemed
                      </div>}
                  </div>
                </div>
              </div>)}
          </div>

          {/* View Treat History Button */}
          <button onClick={() => onNavigate('profile', {
          openSection: 'giftWallet'
        })} className="w-full py-3 bg-black text-[#FFFC00] rounded-2xl font-bold text-base active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-820">
            <Coffee size={18} data-id="element-821" />
            View Treat History
          </button>
        </div>
      </div>;
  }
  // Steps 2 & 3: Item Selection and Recipient Selection (Same Screen)
  if ((step === 2 || step === 3) && selectedStore) {
    const products = PRODUCTS[selectedStore.id] || PRODUCTS['1'];
    const categories = ['All', ...Array.from(new Set(products.map(p => p.category)))];
    const filteredProducts = activeCategory === 'All' ? products : products.filter(p => p.category === activeCategory);
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-[#FFFC00]" data-id="element-822">
        {/* Back Button */}
        <button onClick={handleBack} className="mb-6 w-12 h-12 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-823">
          <ArrowLeft size={24} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-824" />
        </button>

        {/* Main Card */}
        <div className="bg-[#FFF9C4] rounded-3xl p-6 shadow-2xl" data-id="element-825">
          {/* Header */}
          <div className="mb-6" data-id="element-826">
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-827">
              Send a Treat
            </h1>
            <p className="text-gray-700 text-base" data-id="element-828">
              Choose a store and send a treat to friends
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="flex justify-between mb-6" data-id="element-829">
            {[1, 2, 3].map(stepNumber => <div key={stepNumber} className={`flex items-center justify-center rounded-full w-12 h-12 font-bold text-base transition-all ${stepNumber < step ? 'bg-black text-[#FFFC00]' : stepNumber === step ? 'bg-[#FFFC00] text-black border-2 border-black' : 'bg-white/50 text-gray-400'}`} data-id="element-830">
                {stepNumber < step ? <Check size={16} strokeWidth={3} data-id="element-831" /> : stepNumber}
              </div>)}
          </div>

          {/* Step 2: Item Selection */}
          {step === 2 && <div className="animate-in fade-in duration-300" data-id="element-832">
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-833">
                Choose an Item
              </h2>

              {/* Product Grid */}
              <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto mb-6" data-id="element-834">
                {filteredProducts.map(product => <button key={product.id} onClick={() => handleItemSelect(product)} className="bg-white rounded-2xl p-3 text-left active:scale-[0.98] transition-transform shadow-sm" data-id="element-835">
                    <div className="aspect-square rounded-xl bg-gray-100 mb-3 overflow-hidden" data-id="element-836">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-837" />
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1 line-clamp-1" data-id="element-838">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2" data-id="element-839">
                      {product.description}
                    </p>
                    <p className="text-base font-bold text-gray-900" data-id="element-840">
                      ${product.price.toFixed(2)}
                    </p>
                  </button>)}
              </div>
            </div>}

          {/* Step 3: Recipient Selection */}
          {step === 3 && selectedItem && <div className="animate-in fade-in duration-300" data-id="element-841">
              {/* Selected Item Preview */}
              <div className="bg-white rounded-2xl p-4 mb-4 flex items-center gap-3" data-id="element-842">
                <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-843">
                  <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" data-id="element-844" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-845">
                  <h3 className="font-bold text-gray-900 text-base truncate" data-id="element-846">
                    {selectedItem.name}
                  </h3>
                  <p className="text-gray-900 font-bold text-base" data-id="element-847">
                    ${selectedItem.price.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Message Input */}
              <textarea className="w-full rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black p-4 mb-4 text-base resize-none" placeholder="Write a personal message..." value={message} onChange={e => setMessage(e.target.value)} rows={4} data-id="element-848" />

              {/* Select Recipient Heading */}
              <h2 className="text-xl font-bold text-gray-900 mb-4" data-id="element-849">
                Select Recipient
              </h2>

              {/* Recipient Mode Toggle */}
              <div className="flex gap-2 mb-4" data-id="element-850">
                <button onClick={() => setRecipientMode('contacts')} className={`flex-1 py-3 rounded-2xl font-bold text-base transition-colors ${recipientMode === 'contacts' ? 'bg-black text-[#FFFC00]' : 'bg-white text-gray-700'}`} data-id="element-851">
                  From Contacts
                </button>
                <button onClick={() => setRecipientMode('userid')} className={`flex-1 py-3 rounded-2xl font-bold text-base transition-colors ${recipientMode === 'userid' ? 'bg-black text-[#FFFC00]' : 'bg-white text-gray-700'}`} data-id="element-852">
                  Enter User ID
                </button>
              </div>

              {recipientMode === 'contacts' ? <>
                  {/* Search Contacts */}
                  <div className="relative mb-4" data-id="element-853">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-854" />
                    <input type="text" placeholder="Search contacts..." className="w-full pl-12 pr-4 py-3.5 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black text-base" data-id="element-855" />
                  </div>

                  {/* Contacts List */}
                  <div className="space-y-2 max-h-[300px] overflow-y-auto mb-6" data-id="element-856">
                    {mockContacts.map(contact => <button key={contact.id} onClick={() => {
                setRecipient(contact.name);
                setRecipientPhone(contact.phone);
                setStep(4);
              }} className="w-full bg-white rounded-2xl p-4 flex items-center gap-3 text-left active:scale-[0.98] transition-all" data-id="element-857">
                        <div className="w-10 h-10 bg-[#FFFC00] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-858">
                          <User size={20} className="text-gray-900" data-id="element-859" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-860">
                          <p className="font-bold text-gray-900 text-base truncate" data-id="element-861">
                            {contact.name}
                          </p>
                          <p className="text-sm text-gray-500" data-id="element-862">
                            {contact.phone}
                          </p>
                        </div>
                      </button>)}
                  </div>
                </> : <div className="mb-6" data-id="element-863">
                  <input type="text" placeholder="Enter user ID..." value={recipient} onChange={e => setRecipient(e.target.value)} className="w-full py-3.5 px-4 rounded-2xl border-none bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black text-base mb-4" data-id="element-864" />

                  {/* Continue Button for User ID mode */}
                  <button onClick={handleContinueToConfirm} className="w-full py-4 bg-black text-[#FFFC00] rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg" data-id="element-865">
                    Continue
                  </button>
                </div>}
            </div>}
        </div>
      </div>;
  }
  // Step 4: Confirmation
  if (step === 4 && selectedItem && selectedStore) {
    const sliderThumbSize = 48;
    const effectiveProgress = isDragging ? dragProgress : isAnonymous ? 1 : 0;
    const trackBg = `rgb(${Math.round(255 - effectiveProgress * 180)}, ${Math.round(252 - effectiveProgress * 200)}, ${Math.round(0 + effectiveProgress * 120)})`;
    const trackBgStyle = effectiveProgress < 0.5 ? `hsl(${60 - effectiveProgress * 60}, 100%, ${50 + effectiveProgress * 10}%)` : `hsl(${270 + (effectiveProgress - 0.5) * 10}, ${40 + (effectiveProgress - 0.5) * 40}%, ${30 + (1 - effectiveProgress) * 30}%)`;
    return <div className="pb-24 pt-6 px-5 animate-in fade-in duration-500 min-h-screen bg-[#FFFC00]" data-id="element-866">
        {/* Back Button */}
        <button onClick={handleBack} className="mb-6 w-12 h-12 bg-black rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform" data-id="element-867">
          <ArrowLeft size={24} className="text-[#FFFC00]" strokeWidth={2.5} data-id="element-868" />
        </button>

        {/* Confirmation Card */}
        <div className="bg-[#FFF9C4] rounded-3xl p-6 shadow-2xl" data-id="element-869">
          {/* Anonymous Slider */}
          <div className="mb-5" data-id="element-870">
            <p className="text-sm font-semibold text-gray-700 text-center mb-3" data-id="element-871">
              {effectiveProgress > 0.5 ? '🎭 Sending as Secret Admirer' : '😍 Sending as You'}
            </p>
            <div ref={sliderTrackRef} className="relative h-14 rounded-full mx-auto cursor-pointer select-none overflow-hidden" style={{
            maxWidth: 260,
            background: effectiveProgress < 0.3 ? '#FFFC00' : effectiveProgress < 0.7 ? `linear-gradient(90deg, #FFFC00 0%, #7C3AED ${effectiveProgress * 100}%)` : `linear-gradient(90deg, #FFFC00 0%, #7C3AED 60%, #4C1D95 100%)`,
            transition: isDragging ? 'none' : 'background 0.4s ease'
          }} onClick={toggleAnonymous} onPointerDown={handleSliderPointerDown} onPointerMove={handleSliderPointerMove} onPointerUp={handleSliderPointerUp} data-id="element-872">
              {/* Track Labels */}
              <div className="absolute inset-0 flex items-center justify-between px-5 pointer-events-none" data-id="element-873">
                <span className="text-lg transition-opacity duration-300" style={{
                opacity: effectiveProgress < 0.6 ? 1 : 0.3
              }} data-id="element-874">
                  😍
                </span>
                <span className="text-lg transition-opacity duration-300" style={{
                opacity: effectiveProgress > 0.4 ? 1 : 0.3
              }} data-id="element-875">
                  🎭
                </span>
              </div>

              {/* Draggable Thumb */}
              <div className="absolute top-1 rounded-full bg-white shadow-lg flex items-center justify-center pointer-events-none" style={{
              width: sliderThumbSize,
              height: sliderThumbSize,
              left: `calc(${effectiveProgress * 100}% - ${effectiveProgress * sliderThumbSize}px + ${(1 - effectiveProgress) * 4}px + ${effectiveProgress * 4}px)`,
              transition: isDragging ? 'none' : 'left 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
              transform: isDragging ? 'scale(1.1)' : 'scale(1)'
            }} data-id="element-876">
                <span className="text-2xl" data-id="element-877">
                  {effectiveProgress > 0.5 ? '🎭' : '😍'}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-500 text-center mt-2" data-id="element-878">
              {effectiveProgress > 0.5 ? "Recipient won't know you sent this gift. Make it a surprise!" : 'Drag right to send anonymously'}
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6" data-id="element-879">
            {/* Confirm Treat Heading */}
            <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-880">
              Confirm Treat
            </h1>

            {/* Selected Item */}
            <div className="flex items-center gap-4 mb-6" data-id="element-881">
              <div className="w-20 h-20 rounded-2xl bg-gray-100 overflow-hidden flex-shrink-0" data-id="element-882">
                <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" data-id="element-883" />
              </div>
              <div data-id="element-884">
                <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-885">
                  {selectedItem.name}
                </h2>
                <p className="text-lg font-bold text-gray-900" data-id="element-886">
                  ${selectedItem.price.toFixed(2)}
                </p>
              </div>
            </div>

            {/* Sending To */}
            <div className="mb-6" data-id="element-887">
              <p className="text-gray-600 text-base mb-3" data-id="element-888">Sending to:</p>
              <div className="flex items-center gap-3" data-id="element-889">
                <div className="w-12 h-12 bg-[#FFFC00] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-890">
                  <User size={24} className="text-gray-900" data-id="element-891" />
                </div>
                <div data-id="element-892">
                  <p className="font-bold text-gray-900 text-lg" data-id="element-893">{recipient}</p>
                  <p className="text-gray-500 text-sm" data-id="element-894">{recipientPhone}</p>
                </div>
              </div>
              {isAnonymous && <div className="mt-3 bg-purple-50 border border-purple-200 rounded-xl p-3 flex items-center gap-2" data-id="element-895">
                  <span className="text-lg" data-id="element-896">🎭</span>
                  <p className="text-purple-800 text-sm font-medium" data-id="element-897">
                    They'll see this as from <strong data-id="element-898">"A Secret Admirer"</strong>
                  </p>
                </div>}
            </div>

            {/* Expiry Warning */}
            <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-id="element-899">
              <Coffee size={20} className="text-orange-600 flex-shrink-0 mt-0.5" data-id="element-900" />
              <p className="text-orange-800 text-sm font-medium" data-id="element-901">
                Treat will expire in 1 week if not redeemed
              </p>
            </div>

            {/* Send Treat Button */}
            <button onClick={handleSendGift} className="w-full py-4 bg-black text-[#FFFC00] rounded-2xl font-bold text-lg active:scale-[0.98] transition-transform shadow-lg mb-3" data-id="element-902">
              {isAnonymous ? '🎭 Send Secret Treat' : 'Send Treat'}
            </button>

            {/* Choose Different Recipient */}
            <button onClick={() => setStep(3)} className="w-full py-3 text-gray-600 font-semibold text-base" data-id="element-903">
              Choose Different Recipient
            </button>
          </div>
        </div>
      </div>;
  }
  return null;
}